import React, { useEffect } from 'react';

import { formatMoney, toMoney } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, CenteredWorkflowLayout, Flash, Heading, ListRow } from '@betterment/style-closet-react';

import { DirectDepositStepProps, PayCheckAmount } from './data';
import { useTitleDispatch } from './utils';

import { trackClick } from '../../../features/track';
import { navigateOut } from '../../../Shared/path-helpers';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { useAccount } from '../AccountProvider';

interface EditRowProps {
  label: string;
  value?: string;
  onClickEdit: () => void;
  trackingName: string;
  error?: string;
}

function getPayCheckAmountAsText(amount: PayCheckAmount | undefined): string {
  let text = '';
  if (!amount) return text;
  if (amount.type === 'percentage' && amount.value === 100) {
    text = 'Entire paycheck';
  } else if (amount.type === 'percentage') {
    text = `${amount.value}% of paycheck`;
  } else {
    text = `${formatMoney(toMoney(amount.value))} of paycheck`;
  }
  return text;
}

function EditRow({ label, trackingName, value, onClickEdit, error }: EditRowProps) {
  return (
    <>
      <ListRow variant="expanded" leadingTitle={label} leadingSubtitle={value || 'None'}>
        <ListRow.Slot name="trailing">
          <Button
            variant="tertiary"
            type="button"
            aria-label={`Edit ${label}`}
            p="1"
            onClick={() => {
              trackClick({ name: trackingName, module: 'SetUpDirectDeposit' });
              onClickEdit();
            }}
          >
            Edit
          </Button>
        </ListRow.Slot>
      </ListRow>
      {!value && error && (
        <Flash variant="error" mb={6} mt={0}>
          <Text>{error}</Text>
        </Flash>
      )}
    </>
  );
}

export const InitialForm: React.FC<React.PropsWithChildren<DirectDepositStepProps>> = ({ flowState, goToNextStep }) => {
  const { payCheckAmount, employerName } = flowState.values;
  const payCheckAmountString = getPayCheckAmountAsText(payCheckAmount);
  const { account } = useAccount();
  const setTitle = useTitleDispatch();

  useEffect(() => setTitle('Direct deposit form'), [setTitle]);

  const canDownloadForm = employerName && payCheckAmount;
  return (
    <>
      <Heading variant="primary">Download your form</Heading>
      <Text as="p">
        By sharing the form with your employer, you authorize them to take action as described in the form.
      </Text>
      <Box borderBottom="1px solid" borderTop="1px solid" borderColor="bmtGrey20" mt="8">
        <EditRow
          label="Employer Name"
          trackingName="Employer Name"
          onClickEdit={() => goToNextStep({ lastUserEvent: 'selectedEditingEmployer' })}
          value={employerName}
          error="Employer name cannot be blank."
        />
      </Box>
      <Box borderBottom="1px solid" borderColor="bmtGrey20">
        <EditRow
          label="Deposit Amount"
          trackingName="Deposit Amount"
          onClickEdit={() => goToNextStep({ lastUserEvent: 'selectedEditingDeposit' })}
          value={payCheckAmountString}
        />
      </Box>
      <CenteredWorkflowLayout.Actions>
        <Button
          onClick={() => {
            trackClick({ name: 'DownloadDirectDepositForm', module: 'SetupDirectDeposit' });
            navigateOut(
              urlBuilder.apiCheckingAccountDirectDepositPdf.absolute({
                accountId: account.id,
                employer_name: employerName || '',
                amount_units: payCheckAmount?.type ?? '',
                amount_value: payCheckAmount?.value ?? '',
              })
            );
          }}
          disabled={!canDownloadForm}
          type="button"
        >
          Download Form
        </Button>
      </CenteredWorkflowLayout.Actions>
    </>
  );
};
